import React, { useState, useEffect, useCallback } from 'react';
import { dataApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { PresenceInput } from '../../components/ui/presence-input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { useVehicleCollaboration } from '../../hooks/useVehicleCollaboration';
import RecordHistoryButton from '../../components/history/RecordHistoryButton';
import CollaborationIndicator from '../../components/ui/collaboration-indicator';
import { Button } from '../../components/ui/button';
import { ChevronRight } from 'lucide-react';

const VehicleForm = ({ vehicleId, isOpen, onClose }) => {
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [makesModels, setMakesModels] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(false);
  // setSaving removed - collaboration hook autosaves
  const [error, setError] = useState(null);

  // Use collaboration hook - manages vehicle data and realtime sync
  const {
    data: vehicle,
    activeUsers,
    currentUser,
    isOnline,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = useVehicleCollaboration(vehicleId, {});

  const loadVehicleData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const quoteRequestsData = await dataApi.vehicleQuoteRequests(vehicleId);
      setQuoteRequests(quoteRequestsData || []);
    } catch (err) {
      setError('Failed to load vehicle data');
      console.error('Error loading vehicle data:', err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  const loadMakesModels = async () => {
    try {
      const data = await dataApi.makesModelsList();
      setMakesModels(data);
    } catch (err) {
      console.error('Error loading makes and models:', err);
    }
  };

  const loadInsurers = async () => {
    try {
      const data = await dataApi.insurerList();
      setInsurers(data || []);
    } catch (err) {
      console.error('Error loading insurers:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMakesModels();
      loadInsurers();
      if (vehicleId) {
        loadVehicleData();
      } else {
        // No vehicle record exists yet - collaboration hook handles initial data
        setQuoteRequests([]);
      }
    }
  }, [isOpen, vehicleId, loadVehicleData]);

  const handleInputChange = (field, value) => {
    // Update through collaboration hook for realtime sync
    updateField(field, value);
    
    // When make changes to "Other", also set model to "Other" and clear classification
    if (field === 'make' && value === 'Other') {
      updateField('model', 'Other');
      updateField('isLuxury', false);
      updateField('isCommercial', false);
      updateField('isEuropean', false);
      updateField('electricVehicle', false);
      updateField('bodyType', null);
    }
    // When make changes away from "Other", clear otherMake and update classification
    if (field === 'make' && value !== 'Other') {
      updateField('otherMake', null);
      // If model was "Other" because make was "Other", clear it
      if (vehicle?.make === 'Other') {
        updateField('model', '');
        updateField('otherModel', null);
      }
    }
    // When model changes away from "Other", clear otherModel
    if (field === 'model' && value !== 'Other') {
      updateField('otherModel', null);
    }

    // Auto-set classification from vehicle_makes_models when make or model changes
    if ((field === 'make' || field === 'model') && value !== 'Other') {
      const selectedMake = field === 'make' ? value : vehicle?.make;
      const selectedModel = field === 'model' ? value : vehicle?.model;
      if (selectedMake && selectedModel && selectedMake !== 'Other' && selectedModel !== 'Other') {
        const records = makesModels?._records || [];
        const match = records.find(
          r => r.make === selectedMake && r.model === selectedModel
        );
        if (match) {
          updateField('bodyType', match.bodyType || null);
          updateField('isLuxury', Boolean(match.isLuxury));
          updateField('isCommercial', Boolean(match.isCommercial));
          updateField('isEuropean', Boolean(match.isEuropean));
          // Ticked only for a model that is always electric. A model sold both
          // ways (Kona, Outlander) leaves it clear for the shop to tick.
          updateField('electricVehicle', Boolean(match.isElectric));
        }
      }
    }
  };

  const handleInputFocus = (field) => {
    trackFieldEdit(field);
  };

  const handleInputBlur = () => {
    clearFieldEdit();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatRequestNumber = (quote) => {
    const raw = quote?.quoteRequestNumber || quote?.quote_request_number || quote?.requestNumber || quote?.request_number || quote?.$sequence;
    const numeric = parseInt(raw, 10);
    const padded = Number.isFinite(numeric) ? String(Math.max(0, numeric)).padStart(5, '0') : String(raw || '0').padStart(5, '0');
    return `QR-${padded}`;
  };

  const getMakeOptions = () => {
    if (!makesModels || typeof makesModels !== 'object') return [];
    const makes = Object.keys(makesModels).sort();
    const ordered = makes.includes('Other')
      ? ['Other', ...makes.filter((m) => m !== 'Other')]
      : makes;
    return ordered.map(make => ({ value: make, label: make }));
  };

  const getModelOptions = () => {
    if (!vehicle?.make || !makesModels || typeof makesModels !== 'object') return [];
    const models = makesModels[vehicle.make] || [];
    const ordered = models.includes('Other')
      ? ['Other', ...models.filter((m) => m !== 'Other')]
      : models;
    return ordered.map(model => ({ value: model, label: model }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[1100px] max-h-[90vh] flex flex-col animate-none">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>
              {loading ? 'Loading Vehicle...' : 
               error ? 'Vehicle Details - Error' :
               `Vehicle: ${vehicle?.registration || 'New Vehicle'}`}
            </DialogTitle>
            <CollaborationIndicator 
              isOnline={isOnline}
              activeUsers={activeUsers}
              currentUserId={currentUser?.id}
              align="left"
            />
            <RecordHistoryButton rowId={vehicleId} title={vehicle?.registration || 'Vehicle'} />
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {error && (
          <div className="error-state mb-4">
            {error}
          </div>
        )}
        {/* Basic Vehicle Information */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="year"
                    type="number"
                    className="input-borderless"
                    value={vehicle?.year || ''}
                    onChange={(e) => handleInputChange('year', e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                    onFocus={() => handleInputFocus('year')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('year')}
                    isCurrentUser={isCurrentUserEditing('year')}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    disabled={error}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration">Registration</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="registration"
                    type="text"
                    className="input-borderless input-uppercase"
                    value={vehicle?.registration || ''}
                    onChange={(e) => handleInputChange('registration', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                    onFocus={() => handleInputFocus('registration')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('registration')}
                    isCurrentUser={isCurrentUserEditing('registration')}
                    disabled={error}
                  />
                )}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="vin">VIN</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="vin"
                    type="text"
                    className="input-borderless input-uppercase"
                    value={vehicle?.vin || ''}
                    onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17))}
                    onFocus={() => handleInputFocus('vin')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('vin')}
                    isCurrentUser={isCurrentUserEditing('vin')}
                    disabled={error}
                  />
                )}
              </div>

                <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={vehicle?.make || undefined}
                    onValueChange={(value) => handleInputChange('make', value)}
                    disabled={error}
                  >
                    <SelectTrigger className="select-borderless">
                      <SelectValue placeholder="Select Make" />
                    </SelectTrigger>
                    <SelectContent>
                      {getMakeOptions().map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={vehicle?.make === 'Other' ? 'Other' : vehicle?.model || undefined}
                    onValueChange={(value) => handleInputChange('model', value)}
                    disabled={!vehicle?.make || vehicle?.make === 'Other' || error}
                  >
                    <SelectTrigger className="select-borderless">
                      <SelectValue placeholder="Select Model" />
                    </SelectTrigger>
                    <SelectContent>
                      {getModelOptions().map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {vehicle?.make === 'Other' && (
                <div className="space-y-2">
                  <Label htmlFor="otherMake">Specify Make</Label>
                  {loading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <PresenceInput
                      id="otherMake"
                      type="text"
                      className="input-borderless"
                      placeholder="Enter make name"
                      value={vehicle?.otherMake || ''}
                      onChange={(e) => handleInputChange('otherMake', e.target.value)}
                      onFocus={() => handleInputFocus('otherMake')}
                      onBlur={handleInputBlur}
                      editor={getFieldEditor('otherMake')}
                      isCurrentUser={isCurrentUserEditing('otherMake')}
                      disabled={error}
                    />
                  )}
                </div>
              )}

              {vehicle?.model === 'Other' && (
                <div className="space-y-2 sm:col-start-2">
                  <Label htmlFor="otherModel">Specify Model</Label>
                  {loading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <PresenceInput
                      id="otherModel"
                      type="text"
                      className="input-borderless"
                      placeholder="Enter model name"
                      value={vehicle?.otherModel || ''}
                      onChange={(e) => handleInputChange('otherModel', e.target.value)}
                      onFocus={() => handleInputFocus('otherModel')}
                      onBlur={handleInputBlur}
                      editor={getFieldEditor('otherModel')}
                      isCurrentUser={isCurrentUserEditing('otherModel')}
                      disabled={error}
                    />
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="colour">Colour</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="colour"
                    type="text"
                    className="input-borderless"
                    value={vehicle?.colour || ''}
                    onChange={(e) => handleInputChange('colour', e.target.value)}
                    onFocus={() => handleInputFocus('colour')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('colour')}
                    isCurrentUser={isCurrentUserEditing('colour')}
                    disabled={error}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="odometer">Odometer (km)</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="odometer"
                    type="number"
                    className="input-borderless"
                    value={vehicle?.odometer || ''}
                    onChange={(e) => handleInputChange('odometer', e.target.value)}
                    onFocus={() => handleInputFocus('odometer')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('odometer')}
                    isCurrentUser={isCurrentUserEditing('odometer')}
                    min="0"
                    disabled={error}
                  />
                )}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="paint_code">Paint Code</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="paint_code"
                    type="text"
                    className="input-borderless input-uppercase"
                    value={vehicle?.paint_code || ''}
                    onChange={(e) => handleInputChange('paint_code', e.target.value)}
                    onFocus={() => handleInputFocus('paint_code')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('paint_code')}
                    isCurrentUser={isCurrentUserEditing('paint_code')}
                    disabled={error}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WoF and Rego */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>WoF and Rego</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wof_expiry_date">WOF Expiry Date</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="wof_expiry_date"
                    type="date"
                    className="input-borderless"
                    value={vehicle?.wof_expiry_date ? vehicle.wof_expiry_date.split('T')[0] : ''}
                    onChange={(e) => handleInputChange('wof_expiry_date', e.target.value)}
                    onFocus={() => handleInputFocus('wof_expiry_date')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('wof_expiry_date')}
                    isCurrentUser={isCurrentUserEditing('wof_expiry_date')}
                    disabled={error}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="rego_expiry_date">Rego Expiry Date</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="rego_expiry_date"
                    type="date"
                    className="input-borderless"
                    value={vehicle?.rego_expiry_date ? vehicle.rego_expiry_date.split('T')[0] : ''}
                    onChange={(e) => handleInputChange('rego_expiry_date', e.target.value)}
                    onFocus={() => handleInputFocus('rego_expiry_date')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('rego_expiry_date')}
                    isCurrentUser={isCurrentUserEditing('rego_expiry_date')}
                    disabled={error}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Driver & Contact Information */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Driver & Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="form-grid">
              <div className="form-field">
                <Label htmlFor="driver_contact">Driver/Contact Person</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <PresenceInput
                    id="driver_contact"
                    type="text"
                    className="input-borderless"
                    value={vehicle?.driver_contact || ''}
                    onChange={(e) => handleInputChange('driver_contact', e.target.value)}
                    onFocus={() => handleInputFocus('driver_contact')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('driver_contact')}
                    isCurrentUser={isCurrentUserEditing('driver_contact')}
                    placeholder="Driver or contact person for this vehicle"
                    disabled={error}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Features */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Vehicle Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-48" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="electric_vehicle"
                      checked={vehicle?.electric_vehicle || false}
                      onCheckedChange={(checked) => handleInputChange('electric_vehicle', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="electric_vehicle">Electric Vehicle (EV)</Label>
                  </div>
                )}
              </div>

              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-64" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="adas"
                      checked={vehicle?.adas || false}
                      onCheckedChange={(checked) => handleInputChange('adas', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="adas">ADAS (Advanced Driver Assistance Systems)</Label>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Specifications */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Vehicle Specifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paintType">Paint Type</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={vehicle?.paintType || 'Standard'}
                    onValueChange={(value) => handleInputChange('paintType', value)}
                    disabled={error}
                  >
                    <SelectTrigger id="paintType">
                      <SelectValue placeholder="Select paint type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Pearl">Pearl</SelectItem>
                      <SelectItem value="Pearl Plus">Pearl Plus</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bodyType">Body Type</Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={vehicle?.bodyType || ''}
                    onValueChange={(value) => handleInputChange('bodyType', value)}
                    disabled={error}
                  >
                    <SelectTrigger id="bodyType">
                      <SelectValue placeholder="Select body type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedan">Sedan</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="hatchback">Hatchback</SelectItem>
                      <SelectItem value="ute">Ute</SelectItem>
                      <SelectItem value="wagon">Wagon</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="coupe">Coupe</SelectItem>
                      <SelectItem value="people_mover">People Mover</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 items-center">
              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-32" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isLuxury"
                      checked={vehicle?.isLuxury || false}
                      onCheckedChange={(checked) => handleInputChange('isLuxury', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="isLuxury">Luxury</Label>
                  </div>
                )}
              </div>

              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-32" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isCommercial"
                      checked={vehicle?.isCommercial || false}
                      onCheckedChange={(checked) => handleInputChange('isCommercial', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="isCommercial">Commercial</Label>
                  </div>
                )}
              </div>

              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-32" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isEuropean"
                      checked={vehicle?.isEuropean || false}
                      onCheckedChange={(checked) => handleInputChange('isEuropean', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="isEuropean">European</Label>
                  </div>
                )}
              </div>

              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-32" />
                ) : (
                  <div
                    className="flex items-center space-x-2"
                    title="Battery electric or plug-in hybrid — anything with a charge port"
                  >
                    <Checkbox
                      id="electricVehicle"
                      checked={vehicle?.electricVehicle || false}
                      onCheckedChange={(checked) => handleInputChange('electricVehicle', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="electricVehicle">Electric (plug-in)</Label>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insurance */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Insurance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="form-field">
                {loading ? (
                  <Skeleton className="h-5 w-32" />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isInsured"
                      checked={vehicle?.isInsured || false}
                      onCheckedChange={(checked) => handleInputChange('isInsured', checked)}
                      disabled={error}
                    />
                    <Label htmlFor="isInsured">Vehicle is Insured</Label>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="value">Vehicle Value ($)</Label>
                  {loading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <PresenceInput
                      id="value"
                      type="number"
                      className="input-borderless"
                      value={vehicle?.value || ''}
                      onChange={(e) => handleInputChange('value', e.target.value ? Number(e.target.value) : null)}
                      onFocus={() => handleInputFocus('value')}
                      onBlur={handleInputBlur}
                      editor={getFieldEditor('value')}
                      isCurrentUser={isCurrentUserEditing('value')}
                      disabled={error}
                      min="0"
                    />
                  )}
                </div>
              </div>

              {vehicle?.isInsured && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="insurerId">Insurer</Label>
                    {loading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <Select
                        value={vehicle?.insurerId || ''}
                        onValueChange={(value) => handleInputChange('insurerId', value)}
                        disabled={error}
                      >
                        <SelectTrigger id="insurerId">
                          <SelectValue placeholder="Select insurer" />
                        </SelectTrigger>
                        <SelectContent>
                          {insurers.map((insurer) => (
                            <SelectItem key={insurer.$id} value={insurer.$id}>
                              {insurer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sumInsured">Sum Insured ($)</Label>
                    {loading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <PresenceInput
                        id="sumInsured"
                        type="number"
                        className="input-borderless"
                        value={vehicle?.sumInsured || ''}
                        onChange={(e) => handleInputChange('sumInsured', e.target.value ? Number(e.target.value) : null)}
                        onFocus={() => handleInputFocus('sumInsured')}
                        onBlur={handleInputBlur}
                        editor={getFieldEditor('sumInsured')}
                        isCurrentUser={isCurrentUserEditing('sumInsured')}
                        disabled={error}
                        min="0"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insuranceExcess">Excess ($)</Label>
                    {loading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <PresenceInput
                        id="insuranceExcess"
                        type="number"
                        className="input-borderless"
                        value={vehicle?.insuranceExcess || ''}
                        onChange={(e) => handleInputChange('insuranceExcess', e.target.value ? Number(e.target.value) : null)}
                        onFocus={() => handleInputFocus('insuranceExcess')}
                        onBlur={handleInputBlur}
                        editor={getFieldEditor('insuranceExcess')}
                        isCurrentUser={isCurrentUserEditing('insuranceExcess')}
                        disabled={error}
                        min="0"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Related Quote Requests */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle>Related</CardTitle>
          </CardHeader>
          <CardContent>
            <Card className="border">
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Quote Requests ({loading ? '...' : quoteRequests.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {loading ? (
                  <div className="space-y-3 p-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-3/4" />
                  </div>
                ) : quoteRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No quote requests found for this vehicle.</div>
                ) : (
                  quoteRequests.map((quote) => {
                    const requestNumber = formatRequestNumber(quote);
                    return (
                      <div key={quote.id} className="flex items-center justify-between p-3 border rounded-md">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold flex items-center gap-2">
                            <span>{requestNumber}</span>
                            <span className={`text-xs status-${quote.status?.toLowerCase()}`}>{quote.status || 'Unknown'}</span>
                          </div>
                          <div className="text-xs text-muted-foreground flex gap-3">
                            <span>Created: {formatDate(quote.created_at || quote.$createdAt)}</span>
                            <span>Updated: {formatDate(quote.updated_at || quote.$updatedAt)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(`/quote-requests/${quote.id || quote.$id}`, '_blank')}
                            aria-label="Open quote request"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </CardContent>
        </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleForm;